/**
 * Печать раздела страницы.
 *
 * Печатаем не таблицу, а раздел целиком, как он есть на экране: плитки
 * показателей, диаграммы, пояснения и все таблицы. Отчёт уносят на
 * совещание целиком, а не по одной таблице.
 *
 * Как это сделано. Содержимое раздела переносится в отдельное окно вместе
 * со всеми стилями страницы — тогда диаграммы, цвета и вёрстка выглядят
 * ровно так же, и печать не придётся чинить каждый раз, когда раздел
 * поменяется. Экранная обвязка — кнопки, поиск, переключатели — помечена
 * data-noprint и на листе не появляется.
 *
 * Две тонкости, без которых лист выходит не тот:
 *
 * — тема принудительно светлая. Экран у многих тёмный, а чёрные заливки
 *   съедают картридж и читаются плохо;
 * — print-color-adjust: exact. По умолчанию браузер выбрасывает при печати
 *   фоновые заливки, и цветные полосы диаграммы стали бы белыми.
 */
const esc = (v) => String(v !== null && v !== void 0 ? v : '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
/** Дата и время печати в привычном виде. */
const stamp = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
/**
 * Стили страницы для нового окна.
 *
 * Ссылки берём уже разрешёнными в абсолютные: окно открывается на
 * about:blank, и относительный путь вроде «css/style.css» в нём никуда
 * не ведёт.
 */
const collectStyles = () => Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map(el => el.tagName === 'LINK'
    ? `<link rel="stylesheet" href="${esc(el.href)}">`
    : `<style>${el.textContent || ''}</style>`)
    .join('');
/**
 * Дождаться, пока стили подгрузятся.
 *
 * Внешние таблицы стилей грузятся асинхронно, и диалог печати, открытый
 * сразу, показал бы неоформленный текст. Ждём загрузки всех ссылок, но не
 * дольше трёх секунд: недоступный шрифт с внешнего сервера не должен
 * оставлять человека без печати вовсе.
 */
const whenReady = (win) => new Promise(resolve => {
    const links = Array.from(win.document.querySelectorAll('link[rel="stylesheet"]'));
    let left = links.length;
    if (!left)
        return resolve();
    const done = () => {
        left -= 1;
        if (left <= 0)
            resolve();
    };
    links.forEach(l => {
        l.addEventListener('load', done);
        l.addEventListener('error', done);
    });
    setTimeout(resolve, 3000);
});
/**
 * Копия раздела, подготовленная к бумаге.
 *
 * На экране длинные списки живут в блоке с прокруткой — «высота 64vh,
 * overflow: auto». На бумаге прокрутки нет: всё, что не поместилось,
 * просто исчезло бы. Поэтому у копии снимаем ограничения высоты и
 * прокрутку, заданные прямо в разметке; заданные классами снимает
 * правило в стилях печати.
 *
 * Работаем с копией, а не с самим разделом: экран под пользователем
 * меняться не должен.
 */
const forPaper = (node) => {
    const clone = node.cloneNode(true);
    /* Экранную обвязку выбрасываем, а не прячем стилем.
       Спрятанное браузер всё равно разбирает и считает по нему стили, а на
       больших отчётах этого добра больше, чем самого документа: переключатели
       видов, чипы потоков и вторая, экранная копия сетки журнала — она лежит
       рядом с нарезанной по листам. На пятнадцатистраничном журнале ППС это
       примерно половина ячеек, и предпросмотр печати заметно на них думал. */
    clone.querySelectorAll('[data-noprint]').forEach(el => el.remove());
    const free = (el) => {
        el.style.removeProperty('height');
        el.style.removeProperty('max-height');
        el.style.removeProperty('overflow');
        el.style.removeProperty('overflow-x');
        el.style.removeProperty('overflow-y');
    };
    free(clone);
    clone.querySelectorAll('[style]').forEach(free);
    return clone;
};
const PRINT_CSS = (landscape) => `
    @page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 10mm; }

    /* Прокрутка на бумаге невозможна: то, что на экране уезжает вбок или
       вниз, здесь должно раскрыться целиком. */
    * { overflow: visible !important; max-height: none !important; }

    /* Иначе браузер выбрасывает фоновые заливки, и диаграммы печатаются
       белыми — а они здесь и есть содержание. */
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

    html, body {
        margin: 0;
        padding: 0;
        background: #fff !important;
    }

    /* Экранная обвязка на лист не идёт. */
    [data-noprint] { display: none !important; }

    .rep-head { margin: 0 0 10px; font: 13px/1.45 Roboto, Helvetica, Arial, sans-serif; color: #000; }
    .rep-head h1 { margin: 0 0 4px; font-size: 17px; }
    .rep-head p { margin: 0; font-size: 12px; color: #333; }
    .rep-head .rep-stamp { margin-top: 4px; font-size: 11px; color: #666; }

    /* Блоки не рвём между листами: половина диаграммы на предыдущей
       странице бесполезна. */
    section, svg, ul, .rep-sign { break-inside: avoid; page-break-inside: avoid; }
    tr { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }

    .rep-sign { margin-top: 18px; font: 12px Roboto, Helvetica, Arial, sans-serif; }
    .rep-sign span { display: inline-block; min-width: 220px; border-bottom: 1px solid #000; }
`;
export const printNode = ({ node, title, meta, landscape = true, labels }) => {
    if (!node)
        return false;
    const win = window.open('', '_blank');
    /* Окно могли не дать открыть. Возвращаем признак, а не молчим: без
       сообщения человек нажимает кнопку второй и третий раз. */
    if (!win)
        return false;
    const html = '<!doctype html><html lang="ru" data-theme="light"><head><meta charset="utf-8">' +
        `<title>${esc(title)}</title>` +
        collectStyles() +
        `<style>${PRINT_CSS(landscape)}</style>` +
        '</head><body>' +
        '<div class="rep-head">' +
        `<h1>${esc(title)}</h1>` +
        meta
            .filter(Boolean)
            .map(m => `<p>${esc(m)}</p>`)
            .join('') +
        `<p class="rep-stamp">${esc(labels.printedAt)} ${stamp()}</p>` +
        '</div>' +
        forPaper(node).outerHTML +
        `<p class="rep-sign">${esc(labels.signature)} <span></span></p>` +
        '</body></html>';
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    whenReady(win).then(() => {
        /* Ещё один кадр — чтобы браузер успел применить только что
           применённые стили к разметке. */
        win.requestAnimationFrame(() => win.print());
    });
    return true;
};
